/**
 * This file combines and modernizes the functionality of
 * js/core_post_webpack/models/follow_user_api.coffee and
 * js/core_post_webpack/sitewide_business_elements/follow_actions.coffee
 *
 * It hides/unhides and adds click functionality to the buttons in the left sidebar on the profile page.
 */
import { Aedu } from 'global';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import Cookies from 'js-cookie';

const followUser = async (
  user_id: string,
  source: string,
  source_details: string,
) => {
  const res = await postFollow(user_id, 'POST', source, source_details);
  return res;
};

const unfollowUser = async (user_id: string) => {
  const res = await postFollow(user_id, 'DELETE');
  return res;
};

const postFollow = async (
  user_id: string,
  method: 'POST' | 'DELETE',
  source?: string,
  source_details?: string,
) => {
  const data: { source?: string; source_details?: string } = {};
  if (source) {
    data.source = source;
  }
  if (source_details) {
    data.source_details = source_details;
  }

  return fetchWithCsrf(
    routes.api_v0_user_following_index_path({
      user_id,
      subdomain_param: 'api',
    }),
    {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  );
};

const showSignupToFollow = (user_id: string, _name: string) => {
  const cookie_life_span = (1 / 24 / 60) * 30; // thirty minutes, in days
  Cookies.set('follow', user_id, {
    path: '/',
    domain: `.${$app_host}`,
    expires: cookie_life_span,
  });

  // Redirect to signup page
  window.location.href = routes.signup_url({ s: 'follow' });
};

// This copies the jQuery fadeOut function, at the `fast` duration (200ms)
const fadeOut = (element: HTMLElement, callback?: () => void) => {
  element.style.transition = 'opacity 0.2s';
  element.style.opacity = '0';
  setTimeout(() => {
    element.style.display = 'none';
    callback && callback();
  }, 200); // Match the duration of the transition
};

// This copies the jQuery fadeIn function, at the `fast` duration (200ms)
const fadeIn = (element: HTMLElement, callback?: () => void) => {
  element.style.opacity = '0';
  element.style.display = '';
  element.style.transition = 'opacity 0.2s';
  setTimeout(() => {
    element.style.opacity = '1';
    if (callback) {
      setTimeout(callback, 200); // Match the duration of the transition
    }
  }, 10); // Small delay to ensure the transition is applied
};

// exported for use in js/core_post_webpack/sitewide_business_elements/research_interest_list_card/research_interest_list_card.js
export const updateButtons = (
  el: HTMLElement,
  oppositeActionSelector: string,
  success: boolean,
) => {
  // Wait for document-ready so that undo-selector will work
  let oppositeActionElement: HTMLElement | null | undefined;
  if (oppositeActionSelector) {
    const closest = el.closest('div');
    oppositeActionElement = closest?.querySelector(oppositeActionSelector);
  }

  if (success) {
    fadeOut(el, () => {
      if (oppositeActionElement) {
        oppositeActionElement.classList.remove('hidden');
        fadeIn(oppositeActionElement);
      }
    });
  } else {
    if (el) {
      el.style.transition = '';
      el.style.opacity = '1';
      el.style.display = '';
    }
    if (oppositeActionElement) {
      oppositeActionElement.style.transition = '';
      oppositeActionElement.style.opacity = '1';
      oppositeActionElement.style.display = 'none';
    }
  }
};

const updateRelatedButtons = function (
  $el: HTMLElement,
  userId: string,
  originalAction: 'follow' | 'unfollow',
) {
  const dataAttributes =
    originalAction === 'follow'
      ? { original: 'data-follow-user-id', opposite: 'data-unfollow-user-id' }
      : { original: 'data-unfollow-user-id', opposite: 'data-follow-user-id' };

  const relatedButtons = Array.from(
    document.querySelectorAll(`[${dataAttributes.original}='${userId}']`),
  ).filter((otherButton) => otherButton !== $el);

  relatedButtons.forEach((relatedButton) => {
    const closest = relatedButton.closest('div');
    const oppositeButton = closest?.querySelector(
      `[${dataAttributes.opposite}='${userId}']`,
    );

    relatedButton.classList.add('hidden');
    if (oppositeButton) {
      oppositeButton.classList?.remove('hidden');
      (oppositeButton as HTMLElement).style.display = 'block'; // Ensure the button is visible
    }
  });
};

export const setUpLeftSidebarButtons = () => {
  document.addEventListener('DOMContentLoaded', () => {
    const loggedIn = Aedu.User.current && !Aedu.User.current.unconfirmed;
    document.querySelectorAll('[data-follow-user-id]').forEach((element) => {
      element.addEventListener('click', async (e) => {
        e.preventDefault();

        const el = e.currentTarget as HTMLElement;
        const userId = el.getAttribute('data-follow-user-id') as string;

        if (!loggedIn) {
          showSignupToFollow(
            userId,
            el.getAttribute('data-follow-user-fname') as string,
          );
          return;
        }

        const res = await followUser(
          userId,
          el.getAttribute('data-follow-user-source') as string,
          el.getAttribute('data-follow-user-src-details') as string,
        );
        updateButtons(el, '[data-unfollow-user-id]', res.ok);

        res.ok && updateRelatedButtons(el, userId, 'follow');
      });
    });

    document.querySelectorAll('[data-unfollow-user-id]').forEach((element) => {
      element.addEventListener('click', async (e) => {
        if (!loggedIn) {
          return;
        }

        e.preventDefault();

        const el = e.currentTarget as HTMLElement;
        const userId = el.getAttribute('data-unfollow-user-id') as string;

        const res = await unfollowUser(userId);
        updateButtons(el, '[data-follow-user-id]', res.ok);

        res.ok && updateRelatedButtons(el, userId, 'unfollow');
      });
    });
  });
};
